import * as Highcharts from 'highcharts';
import React, {useEffect, useRef, useState} from 'react';
import HighchartsReact from 'highcharts-react-official';
import {Card, CardContent} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Info from '@mui/icons-material/Info';
import {IUoaValue} from '../utils/models';
import makeAverageFromAverage from '../utils/makeAverageFromAverage';
import isNumeric from '../../../utils/isNumeric';
import makeAverage from '../../../utils/makeAverage';
import {COLORS} from '@/utils/constants';

import './styles.scss';

interface IChartProps {
    submissions: any[];
    checkpoints: any[];
    uoas: { id: number; name: string }[];
    selectedUoa: string;
    checkpointId: null | string;
}

const transformations = {
    Avanzato: 5.0,
    Intermedio: 3.8,
    Base: 2.5,
    'In via di prima acquisizione': 1.3,
    Assente: 0,
};

const categories = [
    {name: 'Alfabetica Fuzionale', question: 'q1'},
    {name: 'Culturale', question: 'q6'},
    {name: 'Civica', question: 'q5'},
    {name: 'Emotivo-relazionale', question: 'q4'},
    {name: 'Digitale', question: 'q3'},
];

const getTransformedValue = (val: string | number): number | null => {
    if (typeof val === 'number') return val;
    return transformations[val as keyof typeof transformations] ?? null;
};

const transformSubmission = (submission: any): (IUoaValue | null)[] => {
    return [
        isNumeric(submission.q1) ? {uoa: submission.uoa_id, value: Number(submission.q1)} : null,
        getTransformedValue(submission.q6) != null ? {
            uoa: submission.uoa_id,
            value: getTransformedValue(submission.q6)!
        } : null,
        getTransformedValue(submission.q5) != null ? {
            uoa: submission.uoa_id,
            value: getTransformedValue(submission.q5)!
        } : null,
        getTransformedValue(submission.q4) != null ? {
            uoa: submission.uoa_id,
            value: getTransformedValue(submission.q4)!
        } : null,
        getTransformedValue(submission.q3) != null ? {
            uoa: submission.uoa_id,
            value: getTransformedValue(submission.q3)!
        } : null,
    ];
};

const aggregateCheckpointsData = (submissions: any[], checkpoints: any[], selectedUoa: string): IUoaValue[][][] => {
    return checkpoints.map((checkpoint) => {
        const line = [[], [], [], [], []] as IUoaValue[][];
        submissions.forEach(sub => {
            if ((selectedUoa === 'All' || sub.uoa_id === selectedUoa) && checkpoint.id === sub.checkpoint_id) {
                const values = transformSubmission(sub);
                values.forEach((v, i) => {
                    if (v) line[i].push(v);
                });
            }
        });
        return line;
    });
};

const calculateStatisticalAverages = (
    submissions: any[],
    checkpointId: string | null,
    selectedUoa: string
) => {
    const assenza: number[] = [];
    const presenza: number[] = [];
    const voto: number[] = [];

    submissions.forEach(sub => {
        if ((selectedUoa === 'All' || sub.uoa_id === selectedUoa) && sub.checkpoint_id === Number(checkpointId)) {
            if (sub.domanda1 !== undefined) assenza.push(Number(sub.domanda1) || 0);
            if (sub.giorni_presenza !== undefined) presenza.push(Number(sub.giorni_presenza) || 0);
            if (sub.domanda2 !== undefined) voto.push(Number(sub.domanda2) || 0);
        }
    });

    return {
        assenza: assenza.length ? makeAverage(assenza, 2) : 0,
        presenza: presenza.length ? makeAverage(presenza, 2) : 0,
        voto: voto.length ? makeAverage(voto, 2) : 0,
    };
};

const CompetenzeEducative = ({
                                 submissions,
                                 uoas,
                                 selectedUoa,
                                 checkpoints,
                                 checkpointId,
                             }: IChartProps) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();
    const [giorniAssenza, setGiorniAssenza] = useState<number>(0);
    const [giorniPresenza, setGiorniPresenza] = useState<number>(0);
    const [votoMedio, setVotoMedio] = useState<number>(0);

    useEffect(() => {
        const lines = aggregateCheckpointsData(submissions, checkpoints, selectedUoa);
        const stats = calculateStatisticalAverages(submissions, checkpointId, selectedUoa);

        setGiorniAssenza(stats.assenza);
        setGiorniPresenza(stats.presenza);
        setVotoMedio(stats.voto);

        const series: Highcharts.SeriesOptionsType[] = checkpoints.map((checkpoint, i) => ({
            type: 'line',
            name: checkpoint.name,
            color: Object.values(COLORS)[i] || '',
            data: makeAverageFromAverage(lines[i], selectedUoa, uoas),
        }));

        const chartOpts: Highcharts.Options = {
            chart: {
                polar: true,
                backgroundColor: '#fafafa',
                style: {fontFamily: 'Rubik, sans-serif'},
            },
            title: {text: ''},
            xAxis: {categories: categories.map(c => c.name)},
            yAxis: {
                tickAmount: 6,
                min: 0,
                max: 5,
                labels: {format: '{value:.2f}'},
            },
            series,
        };

        setOptions(chartOpts);
    }, [submissions, selectedUoa, checkpointId]);

    return (
        <div className='competenze-educative'>
            {options && (
                <div className='competenze-educative__charts'>
                    <div>
                        <div className='section-title'>
                            Competenze educative
                            <Tooltip
                                sx={{marginLeft: '0.5rem'}}
                                title={
                                    <div className='space-y-4 px-4'>
                                        {categories.map(({name}) => (
                                            <div key={name}>
                                                <div>{name}</div>
                                                <li>Avanzato = 5.0</li>
                                                <li>Intermedio = 3.8</li>
                                                <li>Base = 2.5</li>
                                                <li>In via di prima acquisizione = 1.3</li>
                                                <li>Assente = 0</li>
                                                <li>Esclusi: "Non dispongo di abbastanza elementi", "Non applicabile"
                                                </li>
                                            </div>
                                        ))}
                                    </div>
                                }
                            >
                                <IconButton>
                                    <Info fontSize='small'/>
                                </IconButton>
                            </Tooltip>
                        </div>
                        <Card>
                            <CardContent>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={options}
                                    ref={chartComponentRef}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    <div>
                        <div className='section-title'>Performance scolastica</div>
                        <div className='competenze-educative__charts_average'>
                            <Card sx={{width: '100%'}}>
                                <CardContent>
                                    <div className='competenze-educative__charts_average_title'>
                                        Giorni Assenza ultimo a.s.
                                    </div>
                                    <div className='competenze-educative__charts_average_value'>
                                        {giorniAssenza}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card sx={{width: '100%'}}>
                                <CardContent>
                                    <div className='competenze-educative__charts_average_title'>
                                        Giorni di presenza alle attività della Fondazione - ultimo a.s
                                    </div>
                                    <div className='competenze-educative__charts_average_value'>
                                        {giorniPresenza}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card sx={{width: '100%'}}>
                                <CardContent>
                                    <div className='competenze-educative__charts_average_title'>
                                        Voto medio
                                    </div>
                                    <div className='competenze-educative__charts_average_value'>
                                        {votoMedio}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompetenzeEducative;
